import React, { useEffect, useMemo, useState } from 'react';
import { ChatbotDataContext } from './ChatbotDataContext';
import analytics from '../analytics/analytics';
let chatRguid = '';
/**
 * Provider component that manages and shares chatbot data and functionality through React Context.
 * Handles everything that doesn't fit into one of the other contexts and/or doesn't change often.
 * @param props - Component props including chat configuration, handlers, and UI state
 * @public
 */
export const ChatbotDataProvider = props => {
  const {
    submitChatMessagesPayload,
    handleMarkerResponse,
    fetchRequestOptions,
    markerComponents,
    logFirstMessageSubmitted,
    logCustomerCarePhoneNumberDisplayed,
    logCustomerCarePhoneNumberClicked,
    enableVoice,
    enableVoiceTooltip,
    sentenceArrayBuilder,
    speechFetcher,
    quickActions,
    quickActionsStackVariant,
    isMobile,
    setiValues,
    debugMode,
    discoveryCitySelected,
    speechFetchRequestOptions,
    transcriptionFetchRequestOptions,
    audioPlayerRef,
    isRealtime,
    isDev,
    setLoadMoreMessages,
    loadMoreMessages,
    shouldDisplayGenHotelFeature,
    componentVersion,
    requestFrom,
    retrieveUrl
  } = props;
  const {
    pushPrompt,
    enabledActions
  } = submitChatMessagesPayload !== null && submitChatMessagesPayload !== void 0 ? submitChatMessagesPayload : {};
  const [errorMessage, setErrorMessage] = useState('');
  const [accountLessAccessToken, setAccountLessAccessToken] = useState('');
  const [accountLessTripsAccessPayload, setAccountLessTripsAccessPayload] = useState();
  // ChatMessages keeps the data that would display to the user
  // Prompts keeps the data that would submit to the api
  const [isDisablingInput, setIsDisablingInputValue] = useState(false);
  const [fullStreamingTexts, setStreamingText] = useState('');
  // Google Maps
  const [locationPinList, appendNewPinToList] = useState([]);
  const [hotelOnMapList, setHotelPinOnMap] = useState([]);
  const setAccountLessAccessTokenMemoFn = useMemo(() => ({
    setAccountLessAccessToken
  }), [setAccountLessAccessToken]);
  const funcValues = useMemo(() => ({
    setErrorMessage,
    setIsDisablingInputValue,
    setStreamingText,
    setLoadMoreMessages,
    // Google Maps
    setHotelPinOnMap: list => {
      // Clear other location pins to focus on hotel pins
      appendNewPinToList([]);
      // Set hotel pins
      setHotelPinOnMap(list);
    },
    appendNewPinToList: ({
      singlePin,
      listOfPins = []
    }) => {
      if (listOfPins.length !== 0) {
        appendNewPinToList([...listOfPins]);
      } else if (singlePin) {
        appendNewPinToList([...locationPinList, singlePin]);
      }
    },
    setAccountLessTripsAccessPayload
  }), [setErrorMessage, setIsDisablingInputValue, setStreamingText, setLoadMoreMessages,
  // Google Maps
  setHotelPinOnMap, appendNewPinToList, setAccountLessTripsAccessPayload]);
  // Preserve chatRguid from submitChatMessagesPayload
  // So on client side, once the Dialog is closed and reopen, there will be a new rguid
  // we need keep to use the same chatRguid.
  if (!chatRguid) {
    chatRguid = submitChatMessagesPayload.rguid;
  } else {
    submitChatMessagesPayload.rguid = chatRguid;
  }
  const accountLessAccessTokensContext = Object.assign({
    accountLessAccessToken
  }, setAccountLessAccessTokenMemoFn);
  const contextValues = Object.assign({
    isSessionActive: true,
    discoveryCitySelected,
    submitChatMessagesPayload,
    handleMarkerResponse,
    fetchRequestOptions,
    markerComponents,
    logFirstMessageSubmitted,
    logCustomerCarePhoneNumberDisplayed,
    logCustomerCarePhoneNumberClicked,
    quickActions,
    quickActionsStackVariant,
    isMobile,
    isRealtime,
    debugMode,
    isDev,
    loadMoreMessages,
    // global state values
    errorMessage,
    isDisablingInput,
    fullStreamingTexts,
    enableVoice,
    enableVoiceTooltip,
    sentenceArrayBuilder,
    speechFetcher,
    setiValues,
    speechFetchRequestOptions,
    transcriptionFetchRequestOptions,
    pushPrompt,
    audioPlayerRef,
    shouldDisplayGenHotelFeature,
    // Google Maps
    locationPinList,
    hotelOnMapList,
    componentVersion,
    shouldUseMapFeature: enabledActions === null || enabledActions === void 0 ? void 0 : enabledActions.shouldEnableInlineLocationMetadata,
    accountLessTripsAccessPayload,
    requestFrom,
    retrieveUrl
  }, funcValues);
  useEffect(() => {
    analytics.init(contextValues);
  }, []);
  return React.createElement(ChatbotDataContext.Provider, {
    value: Object.assign(Object.assign({}, contextValues), accountLessAccessTokensContext)
  }, props.children);
};